import { useObserver, useSubtree } from "subtree.js/react";
import { formatAmount } from "../../../../core/primitives/money";
import { TerminalPaymentActions, TerminalPaymentState } from "./terminal-payment-controller";

/** Takes payment for a customer cart on a Stripe Terminal reader. The page controller connects on open. */
export function TerminalPaymentModal({
  customerCartId,
  fallbackTotalCents,
}: {
  customerCartId: string;
  /** Minor units. Shown before the server has priced the request. */
  fallbackTotalCents: number;
}) {
  const state = useSubtree(TerminalPaymentState);
  const actions = useSubtree(TerminalPaymentActions);
  const observer = useObserver();
  const stage = observer.watch(state.stage);
  const connectedReader = observer.watch(state.connectedReader);
  const amountTotal = observer.watch(state.amountTotal);
  const currency = observer.watch(state.currency);
  const error = observer.watch(state.error);

  const busy = stage === "collecting" || stage === "processing";
  const amount = amountTotal !== null ? formatAmount(amountTotal, currency) : formatAmount(fallbackTotalCents, "USD");

  return (
    <div className="scrim items-center justify-center p-4">
      <div className="modal w-full max-w-md p-6">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-white">Take payment</h2>
            <p className="text-xs text-muted">Card reader · Stripe Terminal</p>
          </div>
          <span className="text-lg font-bold text-accent">{amount}</span>
        </div>

        {stage === "connecting" && <p className="note px-4 py-6 text-center text-muted">Looking for a reader…</p>}

        {connectedReader && stage !== "succeeded" && (
          <p className="mb-3 rounded-btn bg-surface px-3 py-2 text-xs text-muted">
            Connected to <span className="font-medium text-white">{connectedReader.label}</span>
          </p>
        )}

        {/* Every live stage of the transaction wears the accent — this is the one
            screen where the merchant is watching for the reader to respond, and
            splitting "ready" from "collecting" by colour would say nothing that
            the sentence does not. */}
        {stage === "ready" && (
          <p className="note note-accent px-4 py-6 text-center text-accent">Ready. Charge the card when you are.</p>
        )}

        {stage === "collecting" && (
          <p className="note note-accent px-4 py-6 text-center text-accent">Present the card on the reader…</p>
        )}

        {stage === "processing" && (
          <p className="note note-accent px-4 py-6 text-center text-accent">Processing — do not remove the card.</p>
        )}

        {stage === "succeeded" && (
          <p className="note note-accent px-4 py-6 text-center font-medium text-accent">
            Paid. The request updates once Stripe confirms.
          </p>
        )}

        {error && (
          <p role="alert" className="note note-danger mt-3 text-danger">
            {error}
          </p>
        )}

        <div className="mt-5 flex gap-2">
          {stage === "succeeded" ? (
            <button type="button" onClick={actions.close} className="btn-primary btn-sm flex-1">
              Done
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={busy ? () => void actions.cancel() : actions.close}
                className="btn-secondary btn-sm flex-1"
              >
                {busy ? "Cancel" : "Close"}
              </button>
              <button
                type="button"
                onClick={() => void actions.collect(customerCartId)}
                disabled={stage !== "ready"}
                className="btn-primary btn-sm flex-1"
              >
                Charge card
              </button>
            </>
          )}
        </div>

        {stage === "failed" && (
          <div className="mt-3 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => void actions.connect()}
              className="text-xs font-medium text-white underline"
            >
              Search for readers again
            </button>
            <button
              type="button"
              onClick={() => void actions.connect({ simulated: true })}
              className="text-xs text-faint underline"
            >
              Use a simulated reader (testing)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
